import { useEffect, useState, ChangeEvent } from 'react';
import Cookies from 'universal-cookie';
import Converters from './Converters';
import InputField from './InputField';
import { Card, CollasableCard } from './Cards';
import { useTranslation } from 'react-i18next';

const cookies = new Cookies();

interface AlcoholState {
  og: string | number;
  fg: string | number;
  abv: string;
  abw: string;
  att: string;
  cal: string;
}

function calcAlcohol(og: string | number, fg: string | number): AlcoholState {
  const _fg = Converters.blgToGravity(Number(fg));
  const _og = Converters.blgToGravity(Number(og));
  const abv = (76.08 * (_og - _fg) / (1.775 - _og)) * (_fg / 0.794);
  const abw = abv * 0.78924;
  const att = 100 * (_og - _fg) / (_og - 1);
  const cal = (1881.22 * _fg * (_og - _fg) / (1.775 - _og) + 3550 * _fg * ((0.1808 * _og) + (0.8192 * _fg) - 1.0004)) / 0.354882355;
  if (abv < 0) return { og, fg, abv: 'NaN', abw: 'NaN', att: 'NaN', cal: 'NaN' };
  return { og, fg, abv: abv.toFixed(1), abw: abw.toFixed(1), att: att.toFixed(1), cal: cal.toFixed(1) };
}

export default function Alcohol() {
  const { t } = useTranslation();

  useEffect(() => {
    document.title = t('alcohol.pageTitle');
  }, [t]);

  const [state, setState] = useState<AlcoholState>(() =>
    calcAlcohol(cookies.get('alcohol.originalGravity') || 13, cookies.get('alcohol.finalGravity') || 3.6)
  );

  function handleOnChange(event: ChangeEvent<HTMLInputElement>) {
    const { name, value } = event.target;
    if (name === 'og') {
      cookies.set('alcohol.originalGravity', value);
      setState(calcAlcohol(value, state.fg));
    } else {
      cookies.set('alcohol.finalGravity', value);
      setState(calcAlcohol(state.og, value));
    }
  }

  return (
    <div>
      <CollasableCard title={t('alcohol.infoTitle')}>
        <p className="text-muted-foreground text-sm mb-2">
          {t('alcohol.infoLine1')}
        </p>
        <p className="text-muted-foreground text-sm">
          {t('alcohol.infoLine2')}
        </p>
      </CollasableCard>

      <div className="max-w-sm">
        <Card title={t('alcohol.cardTitle')}>
          <InputField name="og" label={t('alcohol.originalGravity')} step="0.1" value={state.og} unit="BLG" onChange={handleOnChange} />
          <InputField name="fg" label={t('alcohol.finalGravity')} step="0.1" value={state.fg} unit="BLG" onChange={handleOnChange} />
          <InputField label={t('alcohol.abv')} value={state.abv} unit="%" readOnly />
          <InputField label={t('alcohol.abw')} value={state.abw} unit="%" readOnly />
          <InputField label={t('alcohol.attenuation')} value={state.att} unit="%" readOnly />
          <InputField label={t('alcohol.calories')} value={state.cal} unit="kcal/l" readOnly />
        </Card>
      </div>
    </div>
  );
}
